import React, { useState, useRef } from "react";
import styles from "./InputDesign.module.css";
import PageEntry from "./PageEntry";
import { Checkbox, FormControlLabel } from "@mui/material";

const PageContent = ({
  selectedPage,
  pages,
  updatePages,
  addPageEntry,
  deletePageEntry,
  addEntry,
  deleteEntry,
  translatePage,
  setSelectedPage,
  setPages,
}) => {
  const [currentLanguage, setCurrentLanguage] = useState("fr");
  const [isUploading, setIsUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef();

  if (selectedPage === null || !pages[selectedPage]) {
    return null;
  }

  const hasAllergens = () => {
    return pages[selectedPage].pageEntries.some(pageEntry => 
      pageEntry.entryType === "dishList" && 
      pageEntry.entries && 
      pageEntry.entries.some(entry => entry.allergens && entry.allergens.length > 0)
    );
  };

  const handleAllergensFilterChange = (event) => {
    const updatedPages = [...pages];
    updatedPages[selectedPage] = {
      ...updatedPages[selectedPage],
      showAllergensFilter: event.target.checked
    };
    updatePages(updatedPages);
  };

  const handlePageTypeChange = (value) => {
    const updatedPages = [...pages];
    updatedPages[selectedPage].pageType[currentLanguage] = value;
    updatePages(updatedPages);
  };

  const updatePageEntry = (pageEntryIndex, updatedPageEntry) => {
    const updatedPages = [...pages];
    updatedPages[selectedPage].pageEntries[pageEntryIndex] = updatedPageEntry;
    updatePages(updatedPages);
  };

  const toggleLanguage = () => {
    setCurrentLanguage(currentLanguage === "fr" ? "en" : "fr");
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }

      const data = await response.json();
      const updatedPages = [...pages];
      updatedPages[selectedPage].pageImgUrl = data.url;
      updatePages(updatedPages);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du téléchargement de l\'image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragStart = (pageEntryIndex) => {
    setDraggedIndex(pageEntryIndex);
  };

  const handleDragOver = (e, pageEntryIndex) => {
    e.preventDefault();
    const draggedItem = pages[selectedPage].pageEntries[draggedIndex];
    if (draggedItem && draggedIndex !== pageEntryIndex) {
      const updatedPages = [...pages];
      const updatedEntries = [...updatedPages[selectedPage].pageEntries];
      updatedEntries.splice(draggedIndex, 1);
      updatedEntries.splice(pageEntryIndex, 0, draggedItem);
      updatedPages[selectedPage].pageEntries = updatedEntries;
      setPages(updatedPages);
      setDraggedIndex(pageEntryIndex);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const transformToPage = (pageEntryIndex) => {
    const pageEntry = pages[selectedPage].pageEntries[pageEntryIndex];
    if (!pageEntry) return;

    // Vérifier s'il y a plus d'un pageEntry dishList
    const dishListCount = pages[selectedPage].pageEntries.filter(
      entry => entry.entryType === "dishList"
    ).length;
    
    if (dishListCount <= 1) return;

    // Créer une nouvelle page avec le contenu du pageEntry
    const newPage = {
      pageType: { fr: pageEntry.entriesTitle?.fr || "", en: pageEntry.entriesTitle?.en || "" },
      pageEntries: [{
        entryType: "dishList",
        entriesTitle: { fr: "", en: "" },
        entries: pageEntry.entries || []
      }]
    };

    // Trouver l'index du premier pageEntry de type dishList
    const firstDishListIndex = pages[selectedPage].pageEntries.findIndex(
      entry => entry.entryType === "dishList"
    );

    // Ajouter la nouvelle page avant ou après la page d'origine selon la position du pageEntry
    const updatedPages = [...pages];
    const insertIndex = pageEntryIndex === firstDishListIndex ? selectedPage : selectedPage + 1;
    updatedPages.splice(insertIndex, 0, newPage);
    setPages(updatedPages);

    // Supprimer l'ancien pageEntry
    updatedPages[selectedPage].pageEntries.splice(pageEntryIndex, 1);
    setPages(updatedPages);
  };

  const transformToPageEntry = () => {
    // Vérifier s'il n'y a qu'un seul pageEntry dishList
    const dishListCount = pages[selectedPage].pageEntries.filter(
      entry => entry.entryType === "dishList"
    ).length;
    
    if (dishListCount !== 1) return;

    // Vérifier s'il y a une page précédente
    if (selectedPage === 0) return;

    const currentPage = pages[selectedPage];
    const dishListEntry = currentPage.pageEntries.find(entry => entry.entryType === "dishList");

    if (!dishListEntry) return;

    // Créer le nouveau pageEntry
    const newPageEntry = {
      entryType: "dishList",
      entriesTitle: { 
        fr: currentPage.pageType.fr || "", 
        en: currentPage.pageType.en || "" 
      },
      entries: dishListEntry.entries || []
    };

    // Ajouter le pageEntry à la page précédente
    const updatedPages = [...pages];
    updatedPages[selectedPage - 1].pageEntries.push(newPageEntry);

    // Supprimer la page actuelle
    updatedPages.splice(selectedPage, 1);
    setPages(updatedPages);
    setSelectedPage(selectedPage - 1);
  };

  const copyToEnglish = () => {
    const updatedPages = [...pages];
    const currentPage = updatedPages[selectedPage];

    // Copier le titre de la page
    if (currentPage.pageType.fr) {
      currentPage.pageType.en = currentPage.pageType.fr;
    }

    // Copier le contenu de chaque entrée
    currentPage.pageEntries.forEach(pageEntry => {
      // Copier le titre de l'entrée
      if (pageEntry.entriesTitle?.fr) {
        pageEntry.entriesTitle.en = pageEntry.entriesTitle.fr;
      }

      // Copier le texte de l'entrée
      if (pageEntry.text?.fr) {
        pageEntry.text.en = pageEntry.text.fr;
      }

      // Copier le contenu des plats
      if (pageEntry.entries) {
        pageEntry.entries.forEach(entry => {
          // Copier toutes les propriétés fr vers en
          Object.keys(entry).forEach(key => {
            if (entry[key]?.fr) {
              entry[key].en = entry[key].fr;
            }
          });
        });
      }
    });

    updatePages(updatedPages);
  };

  // Calculer le nombre de pageEntry dishList
  const dishListCount = pages[selectedPage]?.pageEntries.filter(
    entry => entry.entryType === "dishList"
  ).length || 0;

  return (
    <article className={styles.pageContent}>
      <div className={styles.pageContentHeader}>
        <div className={styles.flexFlexColGap}>
          <h2
            style={{ fontSize: "24px", fontWeight: "bold", color: "#1d192b" }}
          >
            Page Content
          </h2>
          <div className={styles.flexGap}>
            <button onClick={addPageEntry} className={styles.primaryButton}>
              Add Entry
            </button>
            <button onClick={translatePage} className={styles.primaryButton}>
              Translate
            </button>
            <button onClick={copyToEnglish} className={styles.primaryButton}>
              Same in en
            </button>
            <button onClick={toggleLanguage} className={styles.primaryButton}>
              {currentLanguage === "fr" ? "🇬🇧" : "🇫🇷"}
            </button>
            {hasAllergens() && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={pages[selectedPage].showAllergensFilter || false}
                    onChange={handleAllergensFilterChange}
                  />
                }
                label="Afficher filtre allergènes"
              />
            )}
            {dishListCount === 1 && selectedPage > 0 && (
              <button 
                onClick={transformToPageEntry} 
                className={styles.primaryButton}
                style={{
                  backgroundColor: '#FF9800',
                  color: 'white'
                }}
              >
                Fusionner
              </button>
            )}
            <button onClick={() => setSelectedPage(null)} className={styles.primaryButton}>
              ✕
            </button>
          </div>
        </div>
      </div>
      <div className={styles.flex1OverflowYAuto}>
        <div className={styles.flexFlexColGap24}>
          <div className={styles.flexFlexColGap}>
            <div className={styles.flexGap} style={{ alignItems: "center" }}>
              <input
                value={pages[selectedPage].pageType[currentLanguage] || ""}
                onChange={(e) => handlePageTypeChange(e.target.value)}
                placeholder={currentLanguage === "fr" ? "Titre en français" : "English Title"}
                className={styles.inputField}
                style={{ maxWidth: "300px" }}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files[0])}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className={styles.primaryButton}
                style={{ 
                  padding: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "fit-content"
                }}
              >
                {isUploading ? (
                  <span>Téléchargement...</span>
                ) : (
                  <>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    {pages[selectedPage].pageImgUrl && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const updatedPages = [...pages];
                          updatedPages[selectedPage].pageImgUrl = "";
                          updatePages(updatedPages);
                        }}
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                          padding: "0 4px",
                        }}
                      >
                        ×
                      </button>
                    )}
                  </>
                )}
              </button>
              {pages[selectedPage].pageImgUrl && (
                <img
                  src={pages[selectedPage].pageImgUrl}
                  alt=""
                  style={{
                    width: "40px",
                    height: "40px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
              )}
            </div>
          </div>

          {pages[selectedPage].pageEntries.map((pageEntry, pageEntryIndex) => (
            <div key={pageEntryIndex} style={{ position: 'relative' }}>
              <PageEntry
                pageEntry={pageEntry}
                pageEntryIndex={pageEntryIndex}
                deletePageEntry={deletePageEntry}
                addEntry={addEntry}
                deleteEntry={deleteEntry}
                updatePageEntry={updatePageEntry}
                currentLanguage={currentLanguage}
                pages={pages}
                setPages={setPages}
                selectedPage={selectedPage}
                onDragStart={() => handleDragStart(pageEntryIndex)}
                onDragOver={(e) => handleDragOver(e, pageEntryIndex)}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                isDragging={draggedIndex === pageEntryIndex}
              />
              {pageEntry.entryType === "dishList" && dishListCount > 1 && (
                <button
                  onClick={() => transformToPage(pageEntryIndex)}
                  className={styles.primaryButton}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Transformer en page
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </article>
  );
};

export default PageContent;
